// apps/accounting-frontend/src/pages/ProfilePage.tsx
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { identityApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { UserContext } from '../context/UserContext';
import { Button, Input, Label, FormField, LoadingButton } from '@redpanda/ui';
import { setCrossDomainCookie } from '@redpanda/utils';

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext); // Use the context

  // Local state for form inputs
  const [name, setName] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Set initial form state from context
  useEffect(() => {
    if (userContext?.user) {
      setName(userContext.user.name || '');
    }
  }, [userContext?.user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      // Call the API to update
      await identityApi.patch('/auth/me', { name });
      // Refresh user data to update context
      await userContext?.refreshUser();
      toast.success(t('common.messages.updateSuccess'));
    } catch (err) {
      toast.error(t('common.messages.updateFailed'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      toast.error(t('profile.passwordFieldsRequired'));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    try {
      const { data: changed } = await identityApi.post('/auth/change-password', { oldPassword, newPassword });
      // 改密碼後其他裝置的登入會失效，這台換用回傳的新 token（寫回自己網域的 cookie）
      if (changed?.token) setCrossDomainCookie('auth_token', changed.token, '.redpanda-tech.com');
      toast.success(t('profile.passwordChangeSuccess'));
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      if (err.response && err.response.data.message === 'Invalid old password') {
        toast.error(t('profile.invalidOldPassword'));
      } else {
        toast.error(t('profile.passwordChangeError'));
      }
    }
  };

  if (userContext?.isLoading) {
    return <div className="p-8 text-center">{t('common.status.loading')}</div>;
  }

  if (!userContext?.user) {
    return <div className="p-8 text-center text-red-500">{t('profile.fetchError')}</div>;
  }

  const { user } = userContext;

  return (
    <div className="container mx-auto px-10 py-6 pb-32" key={i18n.language}>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('profile.title')}</h1>
      <p className="mt-2 text-gray-600 dark:text-gray-400">{t('profile.subtitle')}</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Profile Information Form */}
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
          <form onSubmit={handleUpdateProfile}>
            <FormField>
              <Label htmlFor="name">{t('profile.name')}</Label>
              <Input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>
            <FormField>
              <Label htmlFor="email">{t('profile.email')}</Label>
              <Input
                type="email"
                id="email"
                value={user?.email || ''}
                disabled
              />
            </FormField>
            <FormField>
              <Label htmlFor="organization">{t('profile.organization')}</Label>
              <Input
                type="text"
                id="organization"
                value={user?.organization?.name || ''}
                disabled
              />
            </FormField>
            <FormField>
              <Label htmlFor="role">{t('profile.role')}</Label>
              <Input
                type="text"
                id="role"
                value={user?.role ? t(`common.roles.${user.role.toLowerCase()}`) : ''}
                disabled
              />
            </FormField>
            <div className="mb-5"></div>
            <LoadingButton
              type="submit"
              loading={isUpdating}
              isEditMode={true}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {t('profile.updateButton')}
            </LoadingButton>
          </form>
        </div>

        {/* Change Password Form */}
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">{t('profile.changePasswordTitle')}</h2>
          <form onSubmit={handleChangePassword}>
            {/* Hidden username field for accessibility */}
            <input
              type="text"
              autoComplete="username"
              value={user?.email || ''}
              style={{ display: 'none' }}
              readOnly
              aria-hidden="true"
            />
            <FormField>
              <Label htmlFor="oldPassword">{t('profile.oldPassword')}</Label>
              <Input
                type="password"
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                autoComplete="current-password"
              />
            </FormField>
            <FormField>
              <Label htmlFor="newPassword">{t('profile.newPassword')}</Label>
              <Input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormField>
            <FormField>
              <Label htmlFor="confirmNewPassword">{t('profile.confirmNewPassword')}</Label>
              <Input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormField>
            <div className="mb-5"></div>
            <Button
              type="submit"
              variant="primary"
              className="w-full"
            >
              {t('profile.changePasswordButton')}
            </Button>
          </form>
        </div>
      </div>

    </div>
  );
};

export default ProfilePage;